import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { reports, accounts, type SubcontoAnalysisRow } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';
import DatePopover from '@/components/DatePopover';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SC_TYPE_LABELS: Record<string, string> = {
  'ref.contractors': 'Контрагент',
  'ref.nomenclature': 'Номенклатура',
  'ref.contracts': 'Договір',
};

export default function SubcontoAnalysisPage() {
  const { t } = useTranslation();
  // Персист ширин колонок ag-Grid у localStorage (дзеркало DataTable-персисту).
  const colW = useGridColumnWidths('subconto-analysis');
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accounts.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });

  const [orgId, setOrgId] = useState('');
  const [accountCode, setAccountCode] = useState('631');
  const [from, setFrom] = useState<string>('2026-02-01T00:00:00+02:00');
  const [to, setTo] = useState<string>('2026-03-01T00:00:00+02:00');

  if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);

  const data = useQuery({
    queryKey: ['subconto-analysis', orgId, accountCode, from, to],
    queryFn: () => reports.subcontoAnalysis({
      organization_id: orgId,
      account_code: accountCode,
      date_from: from,
      date_to: to,
    }),
    enabled: !!orgId && !!accountCode,
  });

  const cols = useMemo<ColDef<SubcontoAnalysisRow>[]>(() => [
    {
      field: 'subconto_type', headerName: t('sa.sc_type'), width: 130, pinned: 'left',
      valueFormatter: (p) => SC_TYPE_LABELS[p.value as string] ?? (p.value as string) ?? '—',
    },
    {
      field: 'subconto_name', headerName: t('sa.sc_name'),
      flex: 1, minWidth: 280, pinned: 'left',
    },
    {
      headerName: t('tb.opening'),
      children: [
        {
          field: 'opening_balance_dt', headerName: t('tb.dt'), width: 130,
          valueFormatter: (p: { value: unknown }) => fmt(p.value as string),
          cellStyle: { textAlign: 'right' },
        },
        {
          field: 'opening_balance_cr', headerName: t('tb.cr'), width: 130,
          valueFormatter: (p: { value: unknown }) => fmt(p.value as string),
          cellStyle: { textAlign: 'right' },
        },
      ],
    },
    {
      headerName: t('tb.turnover'),
      children: [
        {
          field: 'turnover_dt', headerName: t('tb.dt'), width: 130,
          valueFormatter: (p: { value: unknown }) => fmt(p.value as string),
          cellStyle: { textAlign: 'right' },
        },
        {
          field: 'turnover_cr', headerName: t('tb.cr'), width: 130,
          valueFormatter: (p: { value: unknown }) => fmt(p.value as string),
          cellStyle: { textAlign: 'right' },
        },
      ],
    },
    {
      headerName: t('tb.closing'),
      children: [
        {
          field: 'closing_balance_dt', headerName: t('tb.dt'), width: 130,
          valueFormatter: (p: { value: unknown }) => fmt(p.value as string),
          cellStyle: { textAlign: 'right', fontWeight: 600 },
        },
        {
          field: 'closing_balance_cr', headerName: t('tb.cr'), width: 130,
          valueFormatter: (p: { value: unknown }) => fmt(p.value as string),
          cellStyle: { textAlign: 'right', fontWeight: 600 },
        },
      ],
    },
  ], [t]);

  const totals = data.data?.totals;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('sa.title')}</h2>
        <button onClick={() => data.refetch()}>{t('tb.refresh')}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('tb.organization')}</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('ac.account')}</label>
          <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)}>
            {accountsQ.data?.map((a) => (
              <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('tb.from')}</label>
          <DatePopover withTime value={from.slice(0, 16)} onChange={(v) => setFrom(v)} />
        </div>
        <div className="field">
          <label>{t('tb.to')}</label>
          <DatePopover withTime value={to.slice(0, 16)} onChange={(v) => setTo(v)} />
        </div>
      </div>

      <div className="ag-theme-quartz" style={{ height: 'calc(100vh - 380px)', minHeight: 400, marginTop: '1rem' }}>
        <AgGridReact<SubcontoAnalysisRow>
          rowData={data.data?.rows ?? []}
          columnDefs={cols}
          loading={data.isLoading}
          onColumnResized={colW.onColumnResized}
          onGridReady={(e) => { colW.applyTo(e.api); }}
        />
      </div>

      {totals && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', fontWeight: 600 }}>
          <span>
            {t('ac.account')}: <strong>{accountCode}</strong> ({data.data?.account_type})
          </span>
          <span>
            {t('tb.totals')}: т.Дт {fmt(totals.turnover_dt)} · т.Кт {fmt(totals.turnover_cr)} ·{' '}
            к.Дт {fmt(totals.closing_balance_dt)} · к.Кт {fmt(totals.closing_balance_cr)}
          </span>
        </div>
      )}
    </div>
  );
}
